<script lang="ts" setup>
import type { ICareerSubMenu, ICareerSubMenuItem } from './career-subMenu.types'
import { CAREER_SUB_MENU_ITEMS } from './career-subMenu.consts'

const { t } = useI18n()
const { setLazyScrollTo } = useLazyScroll(170)

const menuItems = ref<ICareerSubMenu>(CAREER_SUB_MENU_ITEMS)
const activeIndex = ref(0)
const isScrolling = ref(false)

let observer: IntersectionObserver | null = null
const observerOptions = {
  root: null,
  rootMargin: '-180px 0px -50% 0px',
  threshold: 0,
}

const scrollToSection = (item: ICareerSubMenuItem, index: number) => {
  const hash = t(item.hash)
  history.replaceState(null, '', `#${hash}`)
  setLazyScrollTo(`#${hash}`)
  activeIndex.value = index
  isScrolling.value = true

  setTimeout(() => {
    isScrolling.value = false
  }, 850)
}

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const id = entry.target.id
        const index = menuItems.value.findIndex(item => t(item.hash) === id)

        if (index !== -1) {
          if (isScrolling.value) return

          activeIndex.value = index
        }
      }
    })
  }, observerOptions)

  menuItems.value.forEach((item) => {
    const el = document.getElementById(t(item.hash))
    if (el && observer) {
      observer.observe(el)
    }
  })
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<template>
  <nav class="submenu">
    <div class="submenu__items">
      <div
        v-for="(item, index) in menuItems"
        :key="index"
        :class="{
          'submenu__item': true,
          active: index === activeIndex,
        }"
      >
        <a
          :href="`#${t(item.hash)}`"
          class="submenu__link"
          @click.prevent="scrollToSection(item, index)"
        >
          {{ t(item.label) }}
        </a>
      </div>
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.submenu {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 90;
  display: none;
  width: 100%;
  background: $gray-background;
  border-bottom: 1px solid $gray-300;
  transform: translateY(0);
  transition: transform 0.3s ease-in-out;

  @include atM {
    top: 8rem;
    display: block;
  }
}

.submenu__items {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3.2rem;
  max-width: 156rem;
  height: 8rem;
  margin: 0 auto;
  padding: 2.4rem;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.submenu__item {
  position: relative;
  padding: 0.4rem 0;
  color: $text-400;
  font-weight: normal;
  font-size: 1.4rem;
  line-height: 2.4rem;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: $purple-400;
  }

  &.active {
    color: $text-400;
    border-bottom: 2px solid $purple-400;
  }
}

</style>

